import { useState, useMemo } from 'react';
import { StatusPill } from '@/components/common/StatusPill';
import { formatReportDate, toneForReportStatus } from '@/features/reports/services/reportsPresentation';
import { BarChart3 } from '@/components/common/icons';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';

function isCompletedStatus(status) {
  const s = String(status || '').toLowerCase();
  return s.includes('completed') || s.includes('closed') || s.includes('approved') || s.includes('resolved') || s.includes('posted');
}

function isInProgressStatus(status) {
  const s = String(status || '').toLowerCase();
  return s.includes('in progress') || s.includes('paused') || s.includes('allocated') || s.includes('assigned') || s.includes('planned');
}

function parseDurationMins(str) {
  if (!str) return 0;
  if (typeof str === 'number') return str;
  const raw = String(str).toLowerCase().trim();
  if (!isNaN(raw)) return parseInt(raw, 10);
  if (raw.includes(':')) {
    const parts = raw.split(':');
    const hrs = parseInt(parts[0], 10) || 0;
    const mins = parseInt(parts[1], 10) || 0;
    return hrs * 60 + mins;
  }
  let mins = 0;
  const parts = raw.split(/\s+/);
  let matched = false;
  parts.forEach((p) => {
    const num = parseInt(p, 10);
    if (!isNaN(num)) {
      if (p.includes('h')) {
        mins += num * 60;
        matched = true;
      } else if (p.includes('m')) {
        mins += num;
        matched = true;
      }
    }
  });
  if (!matched && parts.length === 1) {
    const num = parseInt(raw, 10);
    if (!isNaN(num)) return num;
  }
  return mins;
}

export function ProjectReportSection({
  tickets = [],
  selectedProject: externalSelectedProject,
  onProjectChange,
  customStart,
  onCustomStartChange,
  customEnd,
  onCustomEndChange
}) {
  const [internalSelectedProject, setInternalSelectedProject] = useState('');
  const selectedProject = externalSelectedProject !== undefined ? externalSelectedProject : internalSelectedProject;
  const setSelectedProject = (val) => {
    if (onProjectChange) onProjectChange(val);
    setInternalSelectedProject(val);
  };

  const [statusFilter, setStatusFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [search, setSearch] = useState('');

  // Project Report must ONLY include tickets that have a real Project assigned
  const projectTicketsOnly = useMemo(() => {
    return (tickets || []).filter((t) => {
      const p = String(t.Project || t['Project Name'] || t.project || t.projectName || '').trim();
      return Boolean(p && p !== '-' && p.toLowerCase() !== 'none' && p.toLowerCase() !== 'null' && p.toLowerCase() !== 'undefined');
    });
  }, [tickets]);

  // Extract all distinct projects across project tickets
  const distinctProjects = useMemo(() => {
    const set = new Set();
    projectTicketsOnly.forEach((t) => {
      const p = String(t.Project || t['Project Name'] || t.project || t.projectName || '').trim();
      if (p) set.add(p);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [projectTicketsOnly]);

  // Tickets scoped to the actively selected project (or all if none selected)
  const scopedProjectTickets = useMemo(() => {
    if (!selectedProject) return projectTicketsOnly;
    return projectTicketsOnly.filter((t) => {
      const proj = String(t.Project || t['Project Name'] || t.project || t.projectName || '').trim();
      return proj.toLowerCase() === selectedProject.toLowerCase();
    });
  }, [projectTicketsOnly, selectedProject]);

  // Extract distinct employees for the active project scope
  const distinctEmployees = useMemo(() => {
    const set = new Set();
    scopedProjectTickets.forEach((t) => {
      const e = String(t['Employee Name'] || t['Help Person Name'] || t.User || '').trim();
      if (e) set.add(e);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [scopedProjectTickets]);

  // Active project client name(s)
  const activeProjectClientStr = useMemo(() => {
    const set = new Set();
    scopedProjectTickets.forEach((t) => {
      const c = t.Name || t['Client Name'] || t.Client;
      if (c) set.add(c);
    });
    return Array.from(set).join(', ');
  }, [scopedProjectTickets]);

  // Active project team members
  const activeProjectEmployees = useMemo(() => {
    const set = new Set();
    scopedProjectTickets.forEach((t) => {
      const e = t['Employee Name'] || t['Help Person Name'] || t.User;
      if (e) set.add(e);
    });
    return Array.from(set);
  }, [scopedProjectTickets]);

  // Aggregate project statistics strictly for real projects
  const projectStats = useMemo(() => {
    const map = new Map();

    projectTicketsOnly.forEach((t) => {
      const projName = String(t.Project || t['Project Name'] || t.project || t.projectName || '').trim();
      if (!projName) return;

      if (!map.has(projName)) {
        map.set(projName, {
          name: projName,
          total: 0,
          pending: 0,
          inProgress: 0,
          completed: 0,
          clients: new Set(),
          employees: new Set(),
          tickets: []
        });
      }

      const entry = map.get(projName);
      entry.total += 1;
      entry.tickets.push(t);

      const client = t.Name || t['Client Name'] || t.Client;
      if (client) entry.clients.add(client);

      const emp = t['Employee Name'] || t['Help Person Name'] || t.User || t['Employee ID'];
      if (emp) entry.employees.add(emp);

      const st = t.Status;
      if (isCompletedStatus(st)) {
        entry.completed += 1;
      } else if (isInProgressStatus(st)) {
        entry.inProgress += 1;
      } else {
        entry.pending += 1;
      }
    });

    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [projectTicketsOnly]);

  // KPIs strictly for the actively selected project (or overall if All Projects)
  const kpiData = useMemo(() => {
    let totalTickets = 0;
    let pending = 0;
    let inProgress = 0;
    let completed = 0;

    scopedProjectTickets.forEach((t) => {
      totalTickets += 1;
      const st = t.Status;
      if (isCompletedStatus(st)) {
        completed += 1;
      } else if (isInProgressStatus(st)) {
        inProgress += 1;
      } else {
        pending += 1;
      }
    });

    const rate = totalTickets > 0 ? Math.round((completed / totalTickets) * 100) : 0;

    return {
      totalProjects: distinctProjects.length,
      totalTickets,
      pending,
      inProgress,
      completed,
      rate
    };
  }, [scopedProjectTickets, distinctProjects]);

  // Filter individual project tickets with secondary filters (status, employee, search)
  const filteredTickets = useMemo(() => {
    return scopedProjectTickets.filter((t) => {
      const projName = String(t.Project || t['Project Name'] || t.project || t.projectName || '').trim();
      if (!projName) return false;

      if (statusFilter) {
        if (statusFilter === 'Completed' && !isCompletedStatus(t.Status)) return false;
        if (statusFilter === 'In Progress' && !isInProgressStatus(t.Status)) return false;
        if (statusFilter === 'Pending' && (isCompletedStatus(t.Status) || isInProgressStatus(t.Status))) return false;
      }

      if (employeeFilter) {
        const emp = String(t['Employee Name'] || t['Help Person Name'] || t.User || t['Employee ID'] || '').toLowerCase();
        if (!emp.includes(employeeFilter.toLowerCase())) return false;
      }

      if (search.trim()) {
        const q = search.toLowerCase();
        const ticketId = String(t['Ticket ID'] || t.ID || '').toLowerCase();
        const client = String(t.Name || t['Client Name'] || t.Client || '').toLowerCase();
        const desc = String(t['Task Description'] || t.Description || '').toLowerCase();
        const project = projName.toLowerCase();
        const emp = String(t['Employee Name'] || t['Help Person Name'] || '').toLowerCase();
        if (!ticketId.includes(q) && !client.includes(q) && !desc.includes(q) && !project.includes(q) && !emp.includes(q)) {
          return false;
        }
      }

      return true;
    });
  }, [scopedProjectTickets, statusFilter, employeeFilter, search]);

  // Task Allocation Distribution (Donut Chart)
  const taskAllocation = useMemo(() => {
    let closed = 0;
    let inProgress = 0;
    let pending = 0;

    filteredTickets.forEach((t) => {
      const s = t.Status || '';
      if (isCompletedStatus(s)) {
        closed++;
      } else if (isInProgressStatus(s)) {
        inProgress++;
      } else {
        pending++;
      }
    });

    return [
      { name: 'Closed / Completed', value: closed, color: '#10b981' },
      { name: 'In Progress / Paused', value: inProgress, color: '#f59e0b' },
      { name: 'Pending / Open / Rework', value: pending, color: '#f43f5e' }
    ].filter((item) => item.value > 0);
  }, [filteredTickets]);

  // Team Efficiency: Planned Hours vs Actual Logged Hours
  const teamEfficiencyData = useMemo(() => {
    const userMap = {};
    filteredTickets.forEach((t) => {
      const u = String(t['Employee Name'] || t['Help Person Name'] || t.User || t['Employee ID'] || 'Unassigned').trim();
      if (!userMap[u]) userMap[u] = { name: u, planned: 0, actual: 0 };

      const planMins = parseDurationMins(t.TAT || t['Plan Duration'] || t['Planned Duration'] || t.Tat);
      userMap[u].planned += planMins / 60;

      const actMins = parseDurationMins(t['Total Duration'] || t.Duration || t['Actual Duration']);
      userMap[u].actual += actMins / 60;
    });

    return Object.values(userMap)
      .filter((u) => u.name && u.name !== 'Unassigned')
      .sort((a, b) => (b.planned + b.actual) - (a.planned + a.actual))
      .slice(0, 8)
      .map((item) => ({
        ...item,
        planned: parseFloat(item.planned.toFixed(1)),
        actual: parseFloat(item.actual.toFixed(1))
      }));
  }, [filteredTickets]);

  // Ticket Closure Rate Analytics
  const ticketClosureAnalytics = useMemo(() => {
    let total = 0;
    let closed = 0;
    let open = 0;
    let overdue = 0;
    const empMap = {};
    const todayStr = new Date().toISOString().split('T')[0];

    filteredTickets.forEach((t) => {
      total++;
      const isCl = isCompletedStatus(t.Status);
      if (isCl) {
        closed++;
      } else {
        open++;
      }

      const d = String(t['Plan Date'] || t.Date || t.Timestamp || '').slice(0, 10);
      if (d && d < todayStr && !isCl) {
        overdue++;
      }

      const emp = String(t['Employee Name'] || t['Help Person Name'] || t.User || t['Employee ID'] || 'Unassigned').trim();
      if (!empMap[emp]) empMap[emp] = { total: 0, closed: 0 };
      empMap[emp].total++;
      if (isCl) empMap[emp].closed++;
    });

    const rate = total > 0 ? Math.round((closed / total) * 100) : 0;
    const empRows = Object.keys(empMap)
      .map((k) => ({
        name: k,
        total: empMap[k].total,
        closed: empMap[k].closed,
        rate: empMap[k].total > 0 ? Math.round((empMap[k].closed / empMap[k].total) * 100) : 0
      }))
      .sort((a, b) => b.total - a.total);

    return { total, closed, open, overdue, rate, empRows };
  }, [filteredTickets]);

  // Project Completion Rate Analytics
  const projectCompletionAnalytics = useMemo(() => {
    const targetStats = selectedProject
      ? projectStats.filter((p) => p.name.toLowerCase() === selectedProject.toLowerCase())
      : projectStats;

    let completedCount = 0;
    let inProgressCount = 0;
    let pendingCount = 0;

    targetStats.forEach((p) => {
      if (p.total > 0 && p.completed === p.total) {
        completedCount++;
      } else if (p.inProgress > 0 || p.completed > 0) {
        inProgressCount++;
      } else {
        pendingCount++;
      }
    });

    const overallTotal = targetStats.reduce((acc, p) => acc + p.total, 0);
    const overallCompleted = targetStats.reduce((acc, p) => acc + p.completed, 0);
    const rate = overallTotal > 0 ? Math.round((overallCompleted / overallTotal) * 100) : 0;

    const projRows = targetStats.map((p) => ({
      name: p.name,
      total: p.total,
      completed: p.completed,
      rate: p.total > 0 ? Math.round((p.completed / p.total) * 100) : 0
    }));

    return {
      totalProjects: targetStats.length,
      completedCount,
      inProgressCount,
      pendingCount,
      rate,
      projRows
    };
  }, [projectStats, selectedProject]);

  const handleResetFilters = () => {
    setStatusFilter('');
    setEmployeeFilter('');
    setSearch('');
  };

  return (
    <div className="project-report-section" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* ==================== TOP PROJECT SELECTION BAR (MATCHING CLIENT EXPLORER) ==================== */}
      <div
        style={{
          background: '#fff',
          padding: '16px 20px',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                background: '#e0e7ff',
                color: '#4338ca',
                padding: '10px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b', margin: 0 }}>
                Project Report & Explorer
              </h2>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', margin: '4px 0 0 0' }}>
                {selectedProject
                  ? `Report for Project: "${selectedProject}"${activeProjectClientStr ? ` • Client: ${activeProjectClientStr}` : ''}`
                  : 'Select a project from dropdown to generate dedicated reports & performance'}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              id="project-top-selector"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              style={{
                background: '#f8fafc',
                border: '1.5px solid #cbd5e1',
                borderRadius: '10px',
                padding: '10px 16px',
                fontSize: '13px',
                fontWeight: '700',
                color: '#1e293b',
                outline: 'none',
                minWidth: '260px',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <option value="">Select a Project... (All Projects)</option>
              {distinctProjects.map((p) => (
                <option key={p} value={p}>
                  📁 {p}
                </option>
              ))}
            </select>

            {selectedProject && (
              <button
                type="button"
                onClick={() => setSelectedProject('')}
                style={{
                  background: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  borderRadius: '10px',
                  padding: '9px 14px',
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#475569',
                  cursor: 'pointer'
                }}
                title="View All Projects"
              >
                All Projects
              </button>
            )}
          </div>
        </div>

        {/* Selected Project Highlight Ribbon */}
        {selectedProject && (
          <div
            style={{
              background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
              border: '1px solid #bfdbfe',
              borderRadius: '12px',
              padding: '10px 16px',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>📁</span>
              <div>
                <span style={{ fontSize: '14px', fontWeight: '800', color: '#1e3a8a' }}>
                  {selectedProject}
                </span>
                <span style={{ fontSize: '12px', color: '#475569', marginLeft: '10px', fontWeight: '600' }}>
                  Client: <strong>{activeProjectClientStr || 'Direct Client'}</strong>
                </span>
                {activeProjectEmployees.length > 0 && (
                  <span style={{ fontSize: '12px', color: '#475569', marginLeft: '10px', fontWeight: '600' }}>
                    • Team: <strong>{activeProjectEmployees.join(', ')}</strong>
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <StatusPill tone={kpiData.completed === kpiData.totalTickets && kpiData.totalTickets > 0 ? 'success' : kpiData.inProgress > 0 ? 'warning' : 'info'}>
                {kpiData.completed === kpiData.totalTickets && kpiData.totalTickets > 0 ? 'Completed' : kpiData.inProgress > 0 ? 'In Progress' : 'Pending Tasks'}
              </StatusPill>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#1e40af' }}>{kpiData.rate}%</span>
                <div style={{ width: '80px', height: '6px', background: '#bfdbfe', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: `${kpiData.rate}%`, height: '100%', background: kpiData.rate === 100 ? '#16a34a' : '#2563eb' }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Top Summary KPI Cards (Dynamic to Selected Project or All) */}
      <div className="project-kpi-grid">
        <article className="project-kpi-card">
          <div className="dashboard-kpi-card__icon dashboard-kpi-card__icon--purple">
            <BarChart3 className="dashboard-kpi-card__icon-svg" />
          </div>
          <div className="dashboard-kpi-card-content">
            <p className="dashboard-kpi-card__label">{selectedProject ? 'Active Project' : 'Total Projects'}</p>
            <p className="dashboard-kpi-card__value" style={{ fontSize: selectedProject ? '16px' : '22px' }}>
              {selectedProject ? `📁 ${selectedProject}` : kpiData.totalProjects}
            </p>
          </div>
        </article>

        <article className="project-kpi-card">
          <div className="dashboard-kpi-card__icon dashboard-kpi-card__icon--blue">
            <BarChart3 className="dashboard-kpi-card__icon-svg" />
          </div>
          <div className="dashboard-kpi-card-content">
            <p className="dashboard-kpi-card__label">Total Tickets</p>
            <p className="dashboard-kpi-card__value">{kpiData.totalTickets}</p>
          </div>
        </article>

        <article className="project-kpi-card">
          <div className="dashboard-kpi-card__icon dashboard-kpi-card__icon--blue" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
            <BarChart3 className="dashboard-kpi-card__icon-svg" />
          </div>
          <div className="dashboard-kpi-card-content">
            <p className="dashboard-kpi-card__label">In Progress</p>
            <p className="dashboard-kpi-card__value" style={{ color: '#1d4ed8' }}>{kpiData.inProgress}</p>
          </div>
        </article>

        <article className="project-kpi-card">
          <div className="dashboard-kpi-card__icon dashboard-kpi-card__icon--yellow">
            <BarChart3 className="dashboard-kpi-card__icon-svg" />
          </div>
          <div className="dashboard-kpi-card-content">
            <p className="dashboard-kpi-card__label">Pending / Open</p>
            <p className="dashboard-kpi-card__value" style={{ color: '#d97706' }}>{kpiData.pending}</p>
          </div>
        </article>

        <article className="project-kpi-card">
          <div className="dashboard-kpi-card__icon dashboard-kpi-card__icon--green">
            <BarChart3 className="dashboard-kpi-card__icon-svg" />
          </div>
          <div className="dashboard-kpi-card-content">
            <p className="dashboard-kpi-card__label">Completed</p>
            <p className="dashboard-kpi-card__value" style={{ color: '#16a34a' }}>{kpiData.completed}</p>
          </div>
        </article>

        <article className="project-kpi-card">
          <div className="dashboard-kpi-card__icon dashboard-kpi-card__icon--green" style={{ background: '#ecfdf5', color: '#059669' }}>
            <BarChart3 className="dashboard-kpi-card__icon-svg" />
          </div>
          <div className="dashboard-kpi-card-content">
            <p className="dashboard-kpi-card__label">Completion Rate</p>
            <p className="dashboard-kpi-card__value" style={{ color: '#059669' }}>{kpiData.rate}%</p>
          </div>
        </article>
      </div>

      {/* Filter Panel (Single line for Status, Employee, Dates, Search) */}
      <article className="migration-panel migration-panel--full project-report-panel">
        <div className="migration-panel__row">
          <h2>
            {selectedProject ? `Filters for "${selectedProject}"` : 'Project Report Filters'}
          </h2>
          <button type="button" className="attendance-cta attendance-cta--gray" onClick={handleResetFilters}>
            Reset Filters
          </button>
        </div>

        <div
          className="approval-filter-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            gap: '10px',
            marginTop: '8px',
            alignItems: 'flex-end'
          }}
        >
          <label className="dashboard-control" style={{ margin: 0 }}>
            <span>Ticket Status</span>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="In Progress">In Progress</option>
              <option value="Pending">Pending / Open</option>
              <option value="Completed">Completed / Closed</option>
            </select>
          </label>

          <label className="dashboard-control" style={{ margin: 0 }}>
            <span>Employee</span>
            <select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}>
              <option value="">All Employees</option>
              {distinctEmployees.map((emp) => (
                <option key={emp} value={emp}>
                  {emp}
                </option>
              ))}
            </select>
          </label>

          <label className="dashboard-control" style={{ margin: 0 }}>
            <span>Starting Date</span>
            <input type="date" value={customStart} onChange={(e) => onCustomStartChange(e.target.value)} />
          </label>

          <label className="dashboard-control" style={{ margin: 0 }}>
            <span>Ending Date</span>
            <input type="date" value={customEnd} onChange={(e) => onCustomEndChange(e.target.value)} />
          </label>

          <label className="dashboard-control" style={{ margin: 0 }}>
            <span>Search Tickets</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ID, desc, etc..."
              style={{ width: '100%' }}
            />
          </label>
        </div>
      </article>

      {/* ==================== CHARTS & GRAPHS SECTION ==================== */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '14px' }}>
        {/* Task Allocation Distribution (Donut Chart) */}
        <div style={{ background: '#fff', padding: '16px 20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0' }}>
            Task Allocation Distribution {selectedProject ? `(${selectedProject})` : ''}
          </h3>
          <div style={{ height: '200px', position: 'relative' }}>
            {taskAllocation.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '13px' }}>
                No tickets for this project/status
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={taskAllocation}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {taskAllocation.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginTop: '8px' }}>
            {taskAllocation.map((item) => (
              <span key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#475569' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color }} />
                {item.name}: <b>{item.value}</b>
              </span>
            ))}
          </div>
        </div>

        {/* Team Efficiency (Planned vs Actual Hours Bar Chart) */}
        <div style={{ background: '#fff', padding: '16px 20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', margin: '0 0 2px 0' }}>Team Efficiency</h3>
          <p style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8', margin: '0 0 12px 0' }}>
            Planned Hours vs Actual Logged Hours {selectedProject ? `for ${selectedProject}` : ''}
          </p>
          <div style={{ height: '210px' }}>
            {teamEfficiencyData.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '13px' }}>
                No hours logged yet for this selection
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={teamEfficiencyData} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} interval={0} angle={-25} textAnchor="end" />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                  <RechartsTooltip />
                  <Bar dataKey="planned" fill="#6366f1" name="Planned (Hrs)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="actual" fill="#14b8a6" name="Actual (Hrs)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* ==================== TICKET & PROJECT CLOSURE RATE ==================== */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '14px' }}>
        {/* Project Ticket Closure Rate */}
        <div style={{ background: '#fff', padding: '16px 20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Ticket Closure Rate</h3>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8', margin: '2px 0 0 0' }}>
                {selectedProject ? `Closure rate for "${selectedProject}"` : 'All projects closure rate'}
              </p>
            </div>
            <div
              style={{
                fontSize: '20px',
                fontWeight: '900',
                color: ticketClosureAnalytics.rate >= 70 ? '#0d9488' : ticketClosureAnalytics.rate >= 40 ? '#f59e0b' : '#ef4444'
              }}
            >
              {ticketClosureAnalytics.rate}%
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '14px' }}>
            <div style={{ background: '#f8fafc', padding: '8px 6px', borderRadius: '10px', textAlign: 'center', border: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: '17px', fontWeight: '800', color: '#334155' }}>{ticketClosureAnalytics.total}</div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8' }}>Total</div>
            </div>
            <div style={{ background: '#ecfdf5', padding: '8px 6px', borderRadius: '10px', textAlign: 'center', border: '1px solid #a7f3d0' }}>
              <div style={{ fontSize: '17px', fontWeight: '800', color: '#059669' }}>{ticketClosureAnalytics.closed}</div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#059669' }}>Closed</div>
            </div>
            <div style={{ background: '#fff1f2', padding: '8px 6px', borderRadius: '10px', textAlign: 'center', border: '1px solid #fecdd3' }}>
              <div style={{ fontSize: '17px', fontWeight: '800', color: '#e11d48' }}>{ticketClosureAnalytics.open}</div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#e11d48' }}>Open</div>
            </div>
            <div style={{ background: '#fffbeb', padding: '8px 6px', borderRadius: '10px', textAlign: 'center', border: '1px solid #fde68a' }}>
              <div style={{ fontSize: '17px', fontWeight: '800', color: '#d97706' }}>{ticketClosureAnalytics.overdue}</div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#d97706' }}>Overdue</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '160px', overflowY: 'auto' }}>
            {ticketClosureAnalytics.empRows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '16px 0', color: '#94a3b8', fontSize: '12px' }}>No team members assigned</div>
            ) : (
              ticketClosureAnalytics.empRows.map((emp) => (
                <div key={emp.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                  <span style={{ fontWeight: '700', color: '#334155' }}>{emp.name}</span>
                  <span style={{ fontWeight: '600', color: '#64748b' }}>
                    {emp.closed} / {emp.total} (<b style={{ color: emp.rate >= 70 ? '#059669' : '#d97706' }}>{emp.rate}%</b>)
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Project Completion Rate */}
        <div style={{ background: '#fff', padding: '16px 20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Project Completion Rate</h3>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8', margin: '2px 0 0 0' }}>
                {selectedProject ? `Progress for "${selectedProject}"` : 'Overall project progress rate'}
              </p>
            </div>
            <div
              style={{
                fontSize: '20px',
                fontWeight: '900',
                color: projectCompletionAnalytics.rate >= 70 ? '#7c3aed' : projectCompletionAnalytics.rate >= 40 ? '#f59e0b' : '#ef4444'
              }}
            >
              {projectCompletionAnalytics.rate}%
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '14px' }}>
            <div style={{ background: '#f8fafc', padding: '8px 6px', borderRadius: '10px', textAlign: 'center', border: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: '17px', fontWeight: '800', color: '#334155' }}>{projectCompletionAnalytics.totalProjects}</div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8' }}>Projects</div>
            </div>
            <div style={{ background: '#ecfdf5', padding: '8px 6px', borderRadius: '10px', textAlign: 'center', border: '1px solid #a7f3d0' }}>
              <div style={{ fontSize: '17px', fontWeight: '800', color: '#059669' }}>{projectCompletionAnalytics.completedCount}</div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#059669' }}>Completed</div>
            </div>
            <div style={{ background: '#fff1f2', padding: '8px 6px', borderRadius: '10px', textAlign: 'center', border: '1px solid #fecdd3' }}>
              <div style={{ fontSize: '17px', fontWeight: '800', color: '#e11d48' }}>{projectCompletionAnalytics.pendingCount}</div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#e11d48' }}>Pending</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '160px', overflowY: 'auto' }}>
            {projectCompletionAnalytics.projRows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '16px 0', color: '#94a3b8', fontSize: '12px' }}>No projects found</div>
            ) : (
              projectCompletionAnalytics.projRows.map((proj) => (
                <div key={proj.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                  <span style={{ fontWeight: '700', color: '#334155' }}>📁 {proj.name}</span>
                  <span style={{ fontWeight: '600', color: '#64748b' }}>
                    {proj.completed} / {proj.total} (<b style={{ color: proj.rate >= 70 ? '#7c3aed' : '#d97706' }}>{proj.rate}%</b>)
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Project Breakdown Summary Table */}
      <article className="migration-panel migration-panel--full project-report-panel">
        <div className="migration-panel__row">
          <div>
            <h2>Project Performance Overview</h2>
            <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
              {selectedProject
                ? `Performance metrics for project "${selectedProject}"`
                : 'Summary of all projects with ticket completion rates and team allocations'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <StatusPill tone="info">
              {selectedProject ? `1 of ${projectStats.length} Selected` : `${projectStats.length} Projects`}
            </StatusPill>
            {selectedProject && (
              <button
                type="button"
                className="inline-action inline-action--ghost"
                onClick={() => setSelectedProject('')}
                style={{ fontSize: '12px', padding: '3px 10px' }}
              >
                Show All Projects
              </button>
            )}
          </div>
        </div>

        <div className="dashboard-table-wrap" style={{ marginTop: '10px', overflowX: 'auto' }}>
          <table className="dashboard-table reports-premium-table">
            <thead>
              <tr>
                <th style={{ minWidth: '180px' }}>Project Name</th>
                <th style={{ minWidth: '140px' }}>Client(s)</th>
                <th style={{ textAlign: 'center', width: '80px' }}>Total</th>
                <th style={{ textAlign: 'center', width: '100px' }}>In Progress</th>
                <th style={{ textAlign: 'center', width: '90px' }}>Pending</th>
                <th style={{ textAlign: 'center', width: '100px' }}>Completed</th>
                <th style={{ minWidth: '160px' }}>Progress</th>
                <th style={{ minWidth: '160px' }}>Team Members</th>
                <th style={{ textAlign: 'center', width: '110px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {projectStats.length ? (
                projectStats
                  .filter((proj) => !selectedProject || proj.name.toLowerCase() === selectedProject.toLowerCase())
                  .map((proj) => {
                    const pct = proj.total > 0 ? Math.round((proj.completed / proj.total) * 100) : 0;
                    const isSelected = selectedProject && proj.name.toLowerCase() === selectedProject.toLowerCase();

                    return (
                      <tr
                        key={proj.name}
                        style={{
                          backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.08)' : 'inherit'
                        }}
                      >
                        <td>
                          <strong style={{ color: '#1e40af', fontSize: '14px' }}>
                            📁 {proj.name}
                          </strong>
                          {isSelected && (
                            <span style={{ marginLeft: '8px', fontSize: '11px', background: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: '4px', fontWeight: '700' }}>
                              Active
                            </span>
                          )}
                        </td>
                        <td>
                          {Array.from(proj.clients).slice(0, 2).join(', ') || '-'}
                          {proj.clients.size > 2 && ` +${proj.clients.size - 2}`}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{proj.total}</td>
                        <td style={{ textAlign: 'center' }}>
                          {proj.inProgress > 0 ? (
                            <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '12px', fontWeight: 600, fontSize: '12px' }}>
                              {proj.inProgress}
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>0</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {proj.pending > 0 ? (
                            <span style={{ background: '#ffedd5', color: '#c2410c', padding: '2px 8px', borderRadius: '12px', fontWeight: 600, fontSize: '12px' }}>
                              {proj.pending}
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>0</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {proj.completed > 0 ? (
                            <span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '12px', fontWeight: 600, fontSize: '12px' }}>
                              {proj.completed}
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>0</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1, height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                              <div
                                style={{
                                  width: `${pct}%`,
                                  height: '100%',
                                  background: pct === 100 ? '#16a34a' : '#3b82f6',
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }}
                              />
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#334155', minWidth: '32px' }}>
                              {pct}%
                            </span>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', maxHeight: '42px', overflowY: 'auto' }}>
                            {Array.from(proj.employees).slice(0, 3).map((emp) => (
                              <span
                                key={emp}
                                style={{
                                  fontSize: '11px',
                                  background: '#f1f5f9',
                                  color: '#475569',
                                  padding: '1px 6px',
                                  borderRadius: '4px',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {emp}
                              </span>
                            ))}
                            {proj.employees.size > 3 && (
                              <span style={{ fontSize: '11px', color: '#64748b' }}>
                                +{proj.employees.size - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="inline-action inline-action--ghost"
                            style={{
                              padding: '4px 10px',
                              fontSize: '12px',
                              fontWeight: '700',
                              backgroundColor: isSelected ? '#2563eb' : 'transparent',
                              color: isSelected ? '#ffffff' : '#2563eb',
                              border: '1px solid #2563eb',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedProject('');
                              } else {
                                setSelectedProject(proj.name);
                              }
                            }}
                          >
                            {isSelected ? 'View All' : 'Select'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                    No projects found for the selected dates.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </article>

      {/* Detailed Project Tickets Table */}
      <article className="migration-panel migration-panel--full project-report-panel">
        <div className="migration-panel__row">
          <div>
            <h2>Project Tickets Detail</h2>
            <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
              {selectedProject
                ? `Showing tickets for project: "${selectedProject}"`
                : 'Showing all project tickets'}
            </p>
          </div>
          <StatusPill tone="info">{filteredTickets.length} tickets</StatusPill>
        </div>

        <div className="dashboard-table-wrap" style={{ marginTop: '10px', overflowX: 'auto' }}>
          <table className="dashboard-table reports-premium-table">
            <thead>
              <tr>
                <th style={{ minWidth: '100px' }}>Ticket ID</th>
                <th style={{ minWidth: '140px' }}>Project</th>
                <th style={{ minWidth: '140px' }}>Client</th>
                <th style={{ minWidth: '220px' }}>Description</th>
                <th style={{ minWidth: '130px' }}>Assigned To</th>
                <th style={{ width: '90px' }}>Priority</th>
                <th style={{ width: '110px' }}>Status</th>
                <th style={{ width: '100px' }}>Plan Date</th>
                <th style={{ width: '90px' }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.length ? (
                filteredTickets.map((t, idx) => {
                  const projName = t.Project || t['Project Name'] || '';
                  return (
                    <tr key={t['Ticket ID'] || t.ID || idx}>
                      <td>
                        <strong>{t['Ticket ID'] || t.ID}</strong>
                      </td>
                      <td>
                        {projName ? (
                          <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}>
                            📁 {projName}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>-</span>
                        )}
                      </td>
                      <td>{t.Name || t['Client Name'] || t.Client || '-'}</td>
                      <td style={{ maxWidth: '300px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {t['Task Description'] || t.Description || '-'}
                      </td>
                      <td>{t['Employee Name'] || t['Help Person Name'] || t.User || t['Employee ID'] || '-'}</td>
                      <td>
                        <StatusPill tone={toneForReportStatus(t.Priority)}>{t.Priority || 'Normal'}</StatusPill>
                      </td>
                      <td>
                        <StatusPill tone={toneForReportStatus(t.Status)}>{t.Status || 'Open'}</StatusPill>
                      </td>
                      <td>{formatReportDate(t['Plan Date'] || t.Date || t.Timestamp)}</td>
                      <td>{t['Total Duration'] || t.Duration || t.TAT || '-'}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                    No tickets found matching the filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </article>
    </div>
  );
}
